// components/ActiveSwapsSidebar.jsx
import { useState, useRef, useCallback, useEffect } from "react";
import { Box, Stack, Typography, Badge } from "@mui/material";
import {
  Person,
  FiberManualRecord,
  BatteryChargingFull,
  BatteryFull,
  AccessTime,
  SwapHoriz,
  ChevronLeft,
  ChevronRight,
} from "@mui/icons-material";

const STEP_COLORS = {
  DOOR_OPENED_IN: "#0E7C86",
  DOOR_CLOSED_IN: "#6941C6",
  BATTERY_DETECTED_IN: "#1B7F43",
  DOOR_OPENED_OUT: "#0E7C86",
  DOOR_CLOSED_OUT: "#6941C6",
  SUBMITTING: "#B54708",
};

const COLLAPSED_WIDTH = 200;
const EXPANDED_WIDTH = 320;

const COLLAPSED_HEIGHT = 52;
const SNAP_THRESHOLD_RATIO = 0.5;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const SessionCard = ({ session }) => {
  const dotColor = STEP_COLORS[session.step] || "#667085";

  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 2.5,
        bgcolor: "#F9FAFB",
        border: "1px solid #E3E8EE",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 0.75,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Person sx={{ fontSize: 16, color: "#475467" }} />
          <Typography
            noWrap
            sx={{
              fontSize: 12,
              fontWeight: 600,
              color: "#101828",
              maxWidth: 160,
            }}
          >
            {session.userEmail}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <AccessTime sx={{ fontSize: 12, color: "#98A2B3" }} />
          <Typography sx={{ fontSize: 10, color: "#98A2B3" }}>
            {session.timeAgoLabel}
          </Typography>
        </Box>
      </Box>

      <Typography noWrap sx={{ fontSize: 11, color: "#667085", mb: 0.5 }}>
        Driver: {session.driverName}
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
        <FiberManualRecord sx={{ fontSize: 8, color: dotColor }} />
        <Typography sx={{ fontSize: 11, color: dotColor, fontWeight: 600 }}>
          {session.stepLabel}
        </Typography>
        {session.doorNumber !== null && (
          <Typography sx={{ fontSize: 11, color: "#667085" }}>
            — Door {session.doorNumber + 1}
          </Typography>
        )}
      </Box>

      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
        {session.insertedBatteryId && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <BatteryChargingFull sx={{ fontSize: 14, color: "#0E7C86" }} />
            <Typography noWrap sx={{ fontSize: 10, color: "#475467" }}>
              In: {session.insertedBatteryId}
            </Typography>
          </Box>
        )}
        {session.takenBatteryId && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <BatteryFull sx={{ fontSize: 14, color: "#1B7F43" }} />
            <Typography noWrap sx={{ fontSize: 10, color: "#475467" }}>
              Out: {session.takenBatteryId}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default function ActiveSwapsSidebar({ sessions }) {
  const otherSessions = sessions?.filter((s) => !s.isOwnSession) || [];

  const [expanded, setExpanded] = useState(false);
  const [side, setSide] = useState("right");
  const [posY, setPosY] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragX, setDragX] = useState(null);

  const containerRef = useRef(null);
  const dragOffset = useRef({ x: 0, y: 0 });
  const hasMoved = useRef(false);

  const currentWidth = expanded ? EXPANDED_WIDTH : COLLAPSED_WIDTH;

  useEffect(() => {
    if (posY !== null) return;
    setPosY(100);
  }, [posY]);

  const getPointerPos = (e) => {
    if (e.touches && e.touches.length > 0) {
      return { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }
    return { x: e.clientX, y: e.clientY };
  };

  const onDragStart = useCallback(
    (e) => {
      if (e.target.closest("[data-no-drag]")) return;

      const pos = getPointerPos(e);
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;

      dragOffset.current = {
        x: pos.x - rect.left,
        y: pos.y - rect.top,
      };

      hasMoved.current = false;
      setIsDragging(true);
      setDragX(rect.left);

      if (e.type === "mousedown") {
        e.preventDefault();
      }
    },
    [side],
  );

  const onDragMove = useCallback(
    (e) => {
      if (!isDragging) return;

      const pos = getPointerPos(e);
      hasMoved.current = true;

      const newX = pos.x - dragOffset.current.x;
      const newY = pos.y - dragOffset.current.y;

      setDragX(clamp(newX, -currentWidth + 60, window.innerWidth - 60));
      setPosY(clamp(newY, 0, window.innerHeight - COLLAPSED_HEIGHT));
    },
    [isDragging],
  );

  const onDragEnd = useCallback(() => {
    if (!isDragging) return;

    setIsDragging(false);

    if (hasMoved.current && dragX !== null) {
      const center = dragX + currentWidth / 2;
      const threshold = window.innerWidth * SNAP_THRESHOLD_RATIO;

      if (center < threshold) {
        setSide("left");
      } else {
        setSide("right");
      }
    }

    setDragX(null);
  }, [isDragging, dragX]);

  useEffect(() => {
    if (!isDragging) return;

    const onMouseMove = (e) => onDragMove(e);
    const onMouseUp = () => onDragEnd();
    const onTouchMove = (e) => {
      e.preventDefault();
      onDragMove(e);
    };
    const onTouchEnd = () => onDragEnd();

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("touchend", onTouchEnd);

    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
    };
  }, [isDragging, onDragMove, onDragEnd]);

  useEffect(() => {
    const handleResize = () => {
      setPosY((prev) => clamp(prev, 0, window.innerHeight - COLLAPSED_HEIGHT));
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleHeaderClick = () => {
    if (!hasMoved.current) {
      setExpanded((p) => !p);
    }
  };

  if (otherSessions.length === 0) return null;

  const isLeft = side === "left";

  const positionStyles = isDragging
    ? {
        left: dragX,
        right: "auto",
        top: posY ?? 100,
      }
    : {
        left: isLeft ? 0 : "auto",
        right: isLeft ? "auto" : 0,
        top: posY ?? 100,
      };

  const borderRadiusValue = expanded
    ? isLeft
      ? "0 12px 12px 0"
      : "12px 0 0 12px"
    : isLeft
      ? "0 999px 999px 0"
      : "999px 0 0 999px";

  const ChevronIcon = expanded
    ? isLeft
      ? ChevronLeft
      : ChevronRight
    : isLeft
      ? ChevronRight
      : ChevronLeft;

  return (
    <Box
      ref={containerRef}
      sx={{
        position: "fixed",
        ...positionStyles,
        zIndex: 2050,
        width: currentWidth,
        display: "flex",
        flexDirection: "column",
        borderRadius: borderRadiusValue,
        bgcolor: "#FFFFFF",
        border: "2px solid #F9D8A8",
        borderLeft: isLeft && !isDragging ? "none" : "2px solid #F9D8A8",
        borderRight: !isLeft && !isDragging ? "none" : "2px solid #F9D8A8",
        boxShadow: isDragging
          ? "0 8px 40px rgba(16,24,40,0.16)"
          : isLeft
            ? "4px 0 16px rgba(16,24,40,0.08)"
            : "-4px 0 16px rgba(16,24,40,0.08)",
        overflow: "hidden",
        userSelect: isDragging ? "none" : "auto",
        transition: isDragging
          ? "none"
          : "left 0.35s cubic-bezier(0.4,0,0.2,1), right 0.35s cubic-bezier(0.4,0,0.2,1), border-radius 0.3s, box-shadow 0.2s",
        touchAction: "none",
      }}
    >
      <Box
        onMouseDown={onDragStart}
        onTouchStart={onDragStart}
        onClick={handleHeaderClick}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexDirection: isLeft ? "row" : "row-reverse",
          px: 1.5,
          py: 1.25,
          cursor: isDragging ? "grabbing" : "grab",
          userSelect: "none",
          bgcolor: "#FFFCF5",
          borderBottom: expanded ? "1px solid #E3E8EE" : "none",
          "&:hover": { bgcolor: "#FFE9A9" },
        }}
      >
        <ChevronIcon
          sx={{
            fontSize: 20,
            color: "#98A2B3",
            flexShrink: 0,
          }}
        />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flex: 1,
            minWidth: 0,
          }}
        >
          <Badge
            badgeContent={otherSessions.length}
            sx={{
              flexShrink: 0,
              "& .MuiBadge-badge": {
                fontSize: 10,
                fontWeight: 700,
                minWidth: 20,
                height: 20,
                borderRadius: 999,
                bgcolor: "#FFA500",
                color: "#FFFFFF",
              },
            }}
          >
            <SwapHoriz sx={{ fontSize: 20, color: "#344054" }} />
          </Badge>
          <Typography
            noWrap
            sx={{
              fontSize: 14,
              fontWeight: 600,
              color: "#101828",
            }}
          >
            Active Swap{otherSessions.length > 1 ? "s" : ""}
          </Typography>
        </Box>
      </Box>

      {expanded && (
        <Stack
          data-no-drag
          gap={1}
          sx={{
            p: 1.5,
            overflowY: "auto",
            maxHeight: "50vh",
            cursor: "default",
          }}
        >
          {otherSessions.map((session) => (
            <SessionCard key={session.sessionId} session={session} />
          ))}
        </Stack>
      )}
    </Box>
  );
}
